import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const TYPES = ['Résultat', 'Communiqué', 'Événement', 'Urgence', 'Information', 'Autre'];
const TYPE_COLOR = { 'Résultat': 'green', 'Communiqué': 'blue', 'Événement': 'navy', 'Urgence': 'red', 'Information': 'gold', 'Autre': 'gray' };
const AUDIENCES = ['Tous', 'Élèves', 'Parents', 'Enseignants', 'Personnel', 'Administration'];

const EMPTY = {
  title: '', type: 'Information', audience: 'Tous', content: '',
  publishedAt: new Date().toISOString().slice(0, 10), expiresAt: '',
  pinned: false, status: 'Publiée',
};

export default function Annonces() {
  const [tab, setTab] = useState('annonces');
  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'annonces' ? 'active' : ''}`} onClick={() => setTab('annonces')}>Annonces</button>
        <button className={`tab ${tab === 'actualites' ? 'active' : ''}`} onClick={() => setTab('actualites')}>Actualités récentes</button>
      </div>
      {tab === 'annonces'   && <ListeAnnonces />}
      {tab === 'actualites' && <Actualites />}
    </>
  );
}

function ListeAnnonces() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  function load() {
    api.get('/registry/annonces').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })).sort((a, b) => {
        if (b.pinned !== a.pinned) return b.pinned ? 1 : -1;
        return (b.publishedAt || '').localeCompare(a.publishedAt || '');
      }))
    ).catch((e) => setError(e.message));
  }

  useEffect(load, []);

  async function del(id) {
    if (!(await confirmDialog({ title: 'Supprimer', message: 'Supprimer cette annonce ?', confirmLabel: 'Supprimer', danger: true }))) return;
    try { await api.del(`/registry/annonces/${id}`); load(); } catch (e) { setError(e.message); }
  }

  const filtered = rows.filter((r) => !typeFilter || r.type === typeFilter);
  const publiees = rows.filter((r) => r.status === 'Publiée').length;

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Total annonces</span><strong>{rows.length}</strong></div>
        <div className="kpi green"><span>Publiées</span><strong>{publiees}</strong></div>
        <div className="kpi gold"><span>Épinglées</span><strong>{rows.filter((r) => r.pinned).length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Annonces & publications</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 150 }}>
              <option value="">Tous les types</option>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Nouvelle annonce</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucune annonce.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((r) => (
                <div key={r.id} className="panel" style={{ marginBottom: 0, border: r.pinned ? '1px solid var(--primary)' : undefined }}>
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          {r.pinned && <span style={{ fontSize: 14 }}>📌</span>}
                          <span className={`badge ${TYPE_COLOR[r.type] || 'gray'}`}>{r.type}</span>
                          <span className="badge gray">{r.audience || 'Tous'}</span>
                          <span className={`badge ${r.status === 'Publiée' ? 'green' : 'gray'}`}>{r.status}</span>
                        </div>
                        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.title}</h3>
                        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4, marginBottom: 6 }}>
                          {(r.content || '').slice(0, 150)}{r.content?.length > 150 ? '…' : ''}
                        </p>
                        <div style={{ fontSize: 11, color: 'var(--muted-light)' }}>
                          Publié le {r.publishedAt ? new Date(r.publishedAt).toLocaleDateString('fr-FR') : '—'}
                          {r.expiresAt && ` · Expire le ${new Date(r.expiresAt).toLocaleDateString('fr-FR')}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
                        <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Voir</button>
                        <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                        <button className="link-danger" onClick={() => del(r.id)}>✕</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal && <AnnonceModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <AnnonceView item={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />}
    </>
  );
}

function AnnonceView({ item, onClose, onEdit }) {
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <span className={`badge ${TYPE_COLOR[item.type] || 'gray'}`}>{item.type}</span>
          <span className="badge gray">{item.audience || 'Tous'}</span>
          {item.pinned && <span>📌 Épinglée</span>}
        </div>
        <h2>{item.title}</h2>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          Publié le {item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('fr-FR') : '—'}
        </div>
        <div style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', fontSize: 13 }}>{item.content}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 20px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function AnnonceModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.title) { setError('Le titre est obligatoire.'); return; }
    setSaving(true);
    try {
      const payload = { data: f, label: f.title, searchText: `${f.title} ${f.type} ${f.audience}`, refDate: f.publishedAt };
      if (isEdit) await api.patch(`/registry/annonces/${initial.id}`, payload);
      else await api.post('/registry/annonces', payload);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier l\'annonce' : 'Nouvelle annonce'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="grid-3">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Titre *</label><input value={f.title} onChange={set('title')} /></div>
          <div className="field"><label>Type</label>
            <select value={f.type} onChange={set('type')}>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Audience</label>
            <select value={f.audience} onChange={set('audience')}>
              {AUDIENCES.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div className="field"><label>Statut</label>
            <select value={f.status} onChange={set('status')}>
              <option value="Publiée">Publiée</option>
              <option value="Brouillon">Brouillon</option>
              <option value="Archivée">Archivée</option>
            </select>
          </div>
          <div className="field"><label>Date de publication</label><input type="date" value={f.publishedAt} onChange={set('publishedAt')} /></div>
          <div className="field"><label>Date d'expiration</label><input type="date" value={f.expiresAt} onChange={set('expiresAt')} /></div>
          <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={f.pinned} onChange={(e) => setF((p) => ({ ...p, pinned: e.target.checked }))} id="pinned" />
            <label htmlFor="pinned">📌 Épingler en haut</label>
          </div>
        </div>
        <div className="field"><label>Contenu *</label>
          <textarea value={f.content} onChange={set('content')} rows={8} placeholder="Rédigez votre annonce ici…" />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Publier'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function Actualites() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get('/registry/annonces').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setRows(all.filter((r) => r.status === 'Publiée').sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || '')).slice(0, 20));
    }).catch(() => {});
  }, []);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
      {rows.length === 0 ? (
        <div style={{ gridColumn: 'span 3' }}><div className="panel"><div className="empty">Aucune actualité publiée.</div></div></div>
      ) : rows.map((r) => (
        <div key={r.id} className="panel" style={{ cursor: 'pointer', border: r.pinned ? '1px solid var(--primary)' : undefined }}>
          <div style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <span className={`badge ${TYPE_COLOR[r.type] || 'gray'}`} style={{ fontSize: 10 }}>📢 {r.type}</span>
              {r.pinned && <span style={{ fontSize: 14 }}>📌</span>}
            </div>
            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>{r.title}</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
              {(r.content || '').slice(0, 120)}{r.content?.length > 120 ? '…' : ''}
            </p>
            <div style={{ fontSize: 11, color: 'var(--muted-light)', marginTop: 8 }}>
              {r.publishedAt ? new Date(r.publishedAt).toLocaleDateString('fr-FR') : '—'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
